import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { queryKeys } from '~/api/queryKeys';
import type { PipelineStage } from '~/features/experiments/services/experiment-pipeline.service';

import {
  fetchScheduledJobStatus,
  fetchScheduledTriggerStatus,
} from '../services/scheduled-enrichment.service';
import type {
  EnrichmentJobStatus,
  ScheduledTriggerStatusResponse,
} from '../types/scheduled-enrichment.types';

// PDF-specified backoff schedule for scheduled-enrichment polling:
//   < 30 s elapsed     → 3 s
//   30 s – 5 min       → 5 s
//   5 min – hard cap   → 10 s
//   Hard cap: 60 s (pipelines can run 30+ min, keep polling but don't hammer)
const INTERVAL_FAST_MS = 3_000;
const INTERVAL_MEDIUM_MS = 5_000;
const INTERVAL_SLOW_MS = 10_000;
const INTERVAL_CAP_MS = 60_000;
const THRESHOLD_MEDIUM_MS = 30_000;
const THRESHOLD_SLOW_MS = 5 * 60 * 1000;

// AWAITING_APPROVAL is a Meta pipeline terminal state — polling stops, and the
// full-catalog page navigates to the Enrichment Overview. SKIPPED means the
// operator turned this stage off — also terminal, so polling stops rather than
// running until the hard cap.
const TERMINAL_STATUSES = new Set<EnrichmentJobStatus>([
  'COMPLETED',
  'FAILED',
  'AWAITING_APPROVAL',
  'SKIPPED',
]);

/** Backend stage name for the delivery push stage in the Meta Ads pipeline. */
const DELIVERY_STAGE_NAME = 'DELIVERY';

/** Consecutive `FAILED` responses that confirm a failure carrying no evidence. */
const FAILURE_CONFIRMATION_POLLS = 2;

/**
 * Whether a `FAILED` response carries proof that the run actually reached a
 * failure — the backend stamps `failureReason`/`completedAt` when it did.
 *
 * A bare `FAILED` with neither is indistinguishable from the status endpoint
 * answering about a workflow it cannot see yet (just-triggered, not registered),
 * which is why such a response is confirmed by a second poll before it is
 * believed.
 */
function hasFailureEvidence(data: ScheduledTriggerStatusResponse | undefined): boolean {
  return data?.failureReason != null || data?.completedAt != null;
}

/**
 * Polls `GET /enrichments/scheduled/jobs/{jobId}/status` until the DELIVERY
 * pipeline sub-stage reaches `"completed"` (or the overall pipeline fails).
 * Exposes the pipeline stages as a `PipelineStage[]` so `PipelineStageTimeline`
 * can render them directly without an extra mapping step at the call site.
 *
 * Used exclusively by `MetaDeliveryPage` — the delivery step after approval.
 */
export function useScheduledDeliveryStatus(jobId: string | null, enabled = true) {
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    startTimeRef.current = null;
    if (jobId && enabled) startTimeRef.current = Date.now();
  }, [jobId, enabled]);

  const query = useQuery({
    queryKey: queryKeys.scheduledEnrichment.jobStatus(jobId ?? '__disabled__'),
    queryFn: () => {
      if (!jobId) throw new Error('jobId required');
      return fetchScheduledJobStatus(jobId);
    },
    enabled: !!jobId && enabled,
    refetchInterval: (q) => {
      // Stop polling once the request is persistently failing. A 403/404/etc
      // never yields data, so the terminal checks below never fire — without
      // this the poll loops forever. A poll that has already succeeded keeps
      // `status: 'success'` through a transient blip (its data is still cached),
      // so only a never-succeeding poll stops here.
      if (q.state.status === 'error') return false;
      const pipelineStatus = q.state.data?.pipeline?.status;
      const stages = q.state.data?.pipeline?.stages ?? [];
      const deliveryDone =
        stages.find((s) => s.name === DELIVERY_STAGE_NAME)?.status === 'completed';
      if (deliveryDone || pipelineStatus === 'COMPLETED' || pipelineStatus === 'FAILED')
        return false;
      const elapsed = startTimeRef.current != null ? Date.now() - startTimeRef.current : 0;
      if (elapsed < THRESHOLD_MEDIUM_MS) return INTERVAL_FAST_MS;
      if (elapsed < THRESHOLD_SLOW_MS) return INTERVAL_MEDIUM_MS;
      return INTERVAL_SLOW_MS;
    },
    refetchIntervalInBackground: true,
    staleTime: 0,
  });

  const pipelineBlock = query.data?.pipeline;
  const rawStages = pipelineBlock?.stages ?? [];

  // Map ScheduledJobStageStatus → PipelineStage (nullability stripped) so
  // PipelineStageTimeline can consume them without a type cast at the call site.
  const stages: PipelineStage[] = rawStages.map((s) => ({
    name: s.name,
    status: s.status,
    // exactOptionalPropertyTypes: spread only when the value is present so the
    // key is absent rather than set to undefined.
    ...(s.startedAt != null && { startedAt: s.startedAt }),
    ...(s.completedAt != null && { completedAt: s.completedAt }),
  }));

  const deliveryStage = rawStages.find((s) => s.name === DELIVERY_STAGE_NAME);
  const isDeliveryComplete =
    deliveryStage?.status === 'completed' || pipelineBlock?.status === 'COMPLETED';
  const isFailed =
    pipelineBlock?.status === 'FAILED' || rawStages.some((s) => s.status === 'failed');

  return { stages, isDeliveryComplete, isFailed, error: query.error };
}

/**
 * Polls a scheduled trigger workflow's status using the PDF-specified adaptive
 * backoff schedule. Stops when status is COMPLETED or FAILED. The `pollingTimedOut`
 * flag is never set (scheduled runs can legitimately run 30+ min — there is no
 * enforced UI deadline for this flow; the "Still running" message shows on the
 * slow backoff cadence instead).
 *
 * A `FAILED` carrying no failure evidence is not believed on its first sighting:
 * polling continues and the reported status stays `IN_PROGRESS` until a second
 * poll agrees. `FAILED` is a latching, unrecoverable UI state — it halts polling
 * and renders a full-page failure card with a relaunch CTA — so a single
 * unsubstantiated one must not be able to bury a run that is still going. A
 * failure that reports `failureReason`/`completedAt` surfaces immediately, with
 * no extra poll.
 *
 * Pass `enabled = false` to pause polling (e.g. when in experiment mode and
 * the scheduled hooks should be dormant).
 */
export function useScheduledTriggerStatus(workflowId: string | null, enabled = true) {
  // Track elapsed time since the workflowId was first set. Stored in a ref so
  // it doesn't trigger re-renders; the refetchInterval callback reads it on each tick.
  const startTimeRef = useRef<number | null>(null);

  // Consecutive FAILED responses seen for the current workflow. State, not a
  // ref: the reported status flips once the count corroborates the failure, and
  // that has to re-render. Tagged with the workflow it counts for and the
  // response it last counted, so it can be adjusted during render (React's
  // "store information from previous renders" pattern) rather than from an
  // effect — no cascading render, and no stale first render.
  const [failureStreak, setFailureStreak] = useState({
    workflowId,
    dataUpdatedAt: 0,
    count: 0,
  });

  // Reset the backoff clock whenever workflowId or enabled changes. All ref
  // mutations live in effects — not in render — to satisfy React's purity rules.
  useEffect(() => {
    startTimeRef.current = null;
    if (workflowId && enabled) {
      startTimeRef.current = Date.now();
    }
  }, [workflowId, enabled]);

  const query = useQuery({
    queryKey: queryKeys.scheduledEnrichment.triggerStatus(workflowId ?? '__disabled__'),
    queryFn: () => {
      if (!workflowId) throw new Error('workflowId is required');
      return fetchScheduledTriggerStatus(workflowId);
    },
    enabled: !!workflowId && enabled,
    refetchInterval: (query) => {
      // Stop polling once the request is persistently failing. A 403/404/etc
      // never yields a status, so the terminal-status checks below never fire and
      // the poll would loop forever (QA hit continuous 403s on a long-open page).
      // A poll that has already succeeded keeps `status: 'success'` through a
      // transient blip (its data is still cached), so only a never-succeeding
      // poll stops here.
      if (query.state.status === 'error') return false;
      const data = query.state.data;
      const status = data?.status;
      // An unconfirmed FAILED keeps polling on the fast tier so the next
      // response either corroborates it or clears it — latching here is what
      // made a spurious failure unrecoverable without a page reload.
      if (
        status === 'FAILED' &&
        !hasFailureEvidence(data) &&
        failureStreak.count < FAILURE_CONFIRMATION_POLLS
      ) {
        return INTERVAL_FAST_MS;
      }
      // Stop once terminal.
      if (status && TERMINAL_STATUSES.has(status)) return false;
      // Derive elapsed time to select backoff tier.
      const elapsed = startTimeRef.current !== null ? Date.now() - startTimeRef.current : 0;
      if (elapsed < THRESHOLD_MEDIUM_MS) return INTERVAL_FAST_MS;
      if (elapsed < THRESHOLD_SLOW_MS) return INTERVAL_MEDIUM_MS;
      if (elapsed < INTERVAL_CAP_MS * 10) return INTERVAL_SLOW_MS;
      return INTERVAL_CAP_MS;
    },
    refetchIntervalInBackground: true,
    staleTime: 0,
  });

  // Count consecutive FAILED responses off the RAW query data — `query.data` is
  // never masked, only the returned copy is. Keyed on `dataUpdatedAt` so an
  // identical FAILED body still counts as a fresh sighting, and re-keyed on
  // workflowId so a new run starts from zero.
  const rawStatus = query.data?.status;
  let streak = failureStreak;
  if (streak.workflowId !== workflowId) {
    streak = { workflowId, dataUpdatedAt: 0, count: 0 };
    setFailureStreak(streak);
  } else if (query.isSuccess && query.dataUpdatedAt !== streak.dataUpdatedAt) {
    streak = {
      workflowId,
      dataUpdatedAt: query.dataUpdatedAt,
      count: rawStatus === 'FAILED' ? streak.count + 1 : 0,
    };
    setFailureStreak(streak);
  }

  const failureUnconfirmed =
    rawStatus === 'FAILED' &&
    !hasFailureEvidence(query.data) &&
    streak.count < FAILURE_CONFIRMATION_POLLS;

  if (!failureUnconfirmed) return query;

  // Report the run as still in flight until the failure is corroborated. Every
  // other field is passed through untouched, so counts and stages keep updating.
  // The cast restores the discriminated-union type that spreading widens.
  return {
    ...query,
    data: query.data ? { ...query.data, status: 'IN_PROGRESS' as const } : query.data,
  } as typeof query;
}
